import React, { useState } from "react"
import PropTypes from "prop-types"
import { Form, Button, Card, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { CustomForm } from "ui/Form"
import { useWatch } from "antd/es/form/Form"
import ClientDetail from "./ClientDetail"
import ExperienceDetail from "./ExperienceDetail"
import PaymentMethod from "./PaymentMethod"
import PriceBreakdown from "./PriceBreakdown"
import styles from "./BookingForm.module.css"

const { Title } = Typography

const BookingForm = ({ form, bookingUuid, bookAgain, booking, onSubmit, onCancelModal, filterExperienceIds }) => {
  const { t } = useTranslation()
  const [existingClientId, setExistingClientId] = useState(null)
  const mode = "guideBooking"
  const clientName = useWatch("existing_client", form)
  const clientFirstName = useWatch("firstName", form)
  const clientLastName = useWatch("lastName", form)
  const clientEmail = useWatch("email", form)
  const clientPhone = useWatch("phone_number", form)
  const [alertMessage, setAlertMessage] = useState("")
  const isClientDetailsFilled = !!(((clientFirstName && clientLastName) || clientName) && clientEmail && clientPhone) && !alertMessage

  // Amount and breakdown are now calculated and stored in Redux when guest counts or trip duration change
  // No need for reactive calculation here

  return (
    <CustomForm layout="vertical" form={form} onFinish={onSubmit}>
      <div className={styles.scrollContainer}>
        {/* Client Details */}
        <Card className={styles.cardContainer}>
          <Title className={styles.cardTitle} level={5}>
            {t("booking.client_detail")}
          </Title>
          <ClientDetail
            setExistingClientId={setExistingClientId}
            form={form}
            bookingId={bookingUuid}
            alertMessage={alertMessage}
            setAlertMessage={setAlertMessage}
            booking={booking}
          />
        </Card>

        {/* Experience Details */}
        <Card className={styles.cardContainer}>
          <Title className={styles.cardTitle} level={5}>
            {t("booking.experience_details")}
          </Title>
          <ExperienceDetail form={form} bookingId={bookingUuid} booking={booking} bookAgain={bookAgain} mode={mode} filterExperienceIds={filterExperienceIds} />
        </Card>

        {/* Payment Method */}
        {isClientDetailsFilled && (
          <Card className={styles.cardContainer}>
            <Title className={styles.cardTitle} level={5}>
              {t("booking.payment_method")}
            </Title>
            <PaymentMethod
              existingClientId={existingClientId}
              setExistingClientId={setExistingClientId}
              form={form}
              bookingId={bookingUuid}
              booking={booking}
              bookAgain={bookAgain}
            />
          </Card>
        )}

        {/* Price Breakdown */}
        <Card className={styles.cardContainer}>
          <Title className={styles.cardTitle} level={5}>
            {t("booking.price_breakdown")}
          </Title>
          <PriceBreakdown form={form} booking={booking} />
        </Card>
      </div>
      <Form.Item className={styles.formActions}>
        <Space>
          <Button variant="outlined" color="primary" onClick={() => onCancelModal()}>
            {t("global.cancel")}
          </Button>
          <Button type="primary" htmlType="submit">
            {bookingUuid && !bookAgain ? t("global.update") : t("global.submit")}
          </Button>
        </Space>
      </Form.Item>
    </CustomForm>
  )
}

BookingForm.propTypes = {
  form: PropTypes.object.isRequired,
  bookingUuid: PropTypes.string.isRequired,
  bookAgain: PropTypes.object.isRequired,
  booking: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancelModal: PropTypes.func.isRequired,
  filterExperienceIds: PropTypes.arrayOf(PropTypes.number) // Optional - for filtering by equipment
}

export default BookingForm
